/**
 * =====================================================================
 * REGISTRO DO SERVICE WORKER (PWA)
 * ---------------------------------------------------------------------
 * O service worker fica em public/sw.js — escrito à mão, sem Workbox e
 * sem depender da versão do react-scripts.
 *
 * Registrado apenas em produção: em desenvolvimento um service worker
 * ativo serve arquivos de cache e atrapalha o hot reload.
 *
 * Requisito do navegador: PWA só funciona em HTTPS (ou em localhost).
 * Em HTTP puro o registro é ignorado silenciosamente pelo navegador, e
 * por isso a verificação de contexto seguro é feita aqui.
 * =====================================================================
 */

const ehLocalhost = () =>
  Boolean(
    window.location.hostname === 'localhost' ||
      window.location.hostname === '[::1]' ||
      /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname)
  );

/**
 * Registra o service worker.
 *
 * @param {object} config
 * @param {Function} [config.onSuccess]  primeira instalação concluída
 * @param {Function} [config.onUpdate]   nova versão baixada e aguardando
 * @param {Function} [config.onOffline]  app aberto sem rede
 */
export function register(config = {}) {
  if (process.env.NODE_ENV !== 'production') return;

  if (!('serviceWorker' in navigator)) return;

  /** HTTPS ou localhost: fora disso o navegador recusa o registro */
  if (!window.isSecureContext && !ehLocalhost()) return;

  /**
   * Caminho base da aplicação.
   *
   * O CDI é publicado em SUBCAMINHO: o package.json traz
   * "homepage": "https://cdi.assisteweb.com.br/catalogoQAS", e o router usa
   * REACT_APP_BASE_PATH (ver services/history.js). Só o PATHNAME de
   * PUBLIC_URL é aproveitado, e não a URL inteira: assim o registro
   * funciona mesmo que o build seja servido em outro host (QAS, produção,
   * homologação), sem depender do domínio gravado no package.json.
   */
  const baseUrl = new URL(process.env.PUBLIC_URL || '/', window.location.href);
  const baseDir = `${baseUrl.pathname.replace(/\/+$/, '')}/`;

  window.addEventListener('load', () => {
    const swUrl = `${baseDir}sw.js`;

    navigator.serviceWorker
      /**
       * O escopo é o diretório do app. Um service worker em
       * /catalogoQAS/sw.js só controla /catalogoQAS/** — o que é o
       * desejado: nada fora da aplicação é interceptado.
       */
      .register(swUrl, { scope: baseDir })
      .then((registration) => {
        registration.onupdatefound = () => {
          const instalando = registration.installing;

          if (!instalando) return;

          instalando.onstatechange = () => {
            if (instalando.state !== 'installed') return;

            if (navigator.serviceWorker.controller) {
              /** já havia uma versão ativa: esta é uma ATUALIZAÇÃO */
              if (config.onUpdate) config.onUpdate(registration);
            } else {
              /** primeira instalação */
              if (config.onSuccess) config.onSuccess(registration);
            }
          };
        };
      })
      .catch(() => {
        /**
         * Falha no registro não pode derrubar a aplicação: o app segue
         * funcionando normalmente, apenas sem o modo instalável.
         */
      });

    if (ehLocalhost() && config.onOffline) {
      /** em localhost, verifica se o SW ainda existe no servidor */
      fetch(swUrl, { headers: { 'Service-Worker': 'script' } }).catch(() =>
        config.onOffline()
      );
    }
  });
}

/**
 * Aplica imediatamente uma versão nova que está aguardando e recarrega a
 * página. Chamar a partir do aviso de atualização.
 */
export function applyUpdate(registration) {
  if (!registration || !registration.waiting) {
    window.location.reload();
    return;
  }

  registration.waiting.postMessage({ type: 'SKIP_WAITING' });

  /** recarrega quando o novo service worker assumir o controle */
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}

/**
 * Remove o service worker e limpa os caches do CDI.
 *
 * Use como saída de emergência: chamar no lugar de register() desativa o
 * PWA para quem já tinha o app instalado, sem precisar esperar a
 * expiração de cache.
 */
export function unregister() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .then(() => {
      if (!window.caches) return null;

      return caches
        .keys()
        .then((nomes) =>
          Promise.all(
            nomes
              .filter((nome) => nome.startsWith('cdi-'))
              .map((nome) => caches.delete(nome))
          )
        );
    })
    .catch(() => {
      /** silencioso: desregistrar é operação best-effort */
    });
}
